/** Error body returned by every API endpoint on failure. */
export type ApiErrorCode =
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'VALIDATION'
  | 'CONFLICT'
  | 'BAD_REQUEST'
  | 'INTERNAL'
  // Getting in (public pages): a link that no longer works, a wrong join code, a taken username, too many tries
  | 'INVITE_INVALID'
  | 'JOIN_CODE_INVALID'
  | 'USERNAME_TAKEN'
  | 'RATE_LIMITED'
  // Time and attendance: no employee record behind the account; a clock QR that is not today's (or a moment old)
  | 'NOT_EMPLOYEE'
  | 'CLOCK_CODE_INVALID'
  // A clock screen's link that is unknown or revoked
  | 'DISPLAY_INVALID'
  // The PIN (pin.ts): none set yet, or the session locked until it is typed; only `/api/me` and the PIN answer until then.
  // A wrong one says how many tries are left
  | 'PIN_SETUP'
  | 'PIN_LOCKED'
  | 'PIN_WRONG'
  // A super admin looking at the apps as someone (view-as): they read, and change nothing
  | 'VIEW_ONLY'

export interface ApiIssue {
  path: (string | number)[]
  message: string
}

export interface ApiErrorBody {
  error: {
    code: ApiErrorCode
    message: string
    issues?: ApiIssue[]
  }
}

export const httpStatusOf: Record<ApiErrorCode, number> = {
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  VALIDATION: 422,
  CONFLICT: 409,
  BAD_REQUEST: 400,
  INTERNAL: 500,
  INVITE_INVALID: 404,
  JOIN_CODE_INVALID: 404,
  USERNAME_TAKEN: 409,
  RATE_LIMITED: 429,
  NOT_EMPLOYEE: 409,
  CLOCK_CODE_INVALID: 422,
  DISPLAY_INVALID: 404,
  PIN_SETUP: 403,
  PIN_LOCKED: 403,
  PIN_WRONG: 403,
  VIEW_ONLY: 403,
}

export class ApiError extends Error {
  constructor(
    readonly code: ApiErrorCode,
    message: string,
    readonly issues?: ApiIssue[],
  ) {
    super(message)
    this.name = 'ApiError'
  }

  get status(): number {
    return httpStatusOf[this.code]
  }

  toBody(): ApiErrorBody {
    return { error: { code: this.code, message: this.message, ...(this.issues && { issues: this.issues }) } }
  }
}
